import {
  CONTENT_REQUEST_SPEC_CORE_KEYS,
  CONTENT_REQUEST_SPEC_PARAM_KEYS,
  type ContentRequestSpec,
  type ContentRequestSpecParamKey,
} from "@newform/constants";
import type { RouterOutput } from "@/lib/trpc-client";
import type { CreatorPortalAccess } from "../creatorAuthContext";
import { portalHref } from "../useCreatorPortalAccess";

export type CreatorFeedItem =
  RouterOutput["creatorPortal"]["getOpenRequests"][number];
export type CreatorRequestDetail =
  RouterOutput["creatorPortal"]["getOpenRequest"];
export type RequestExampleAd = CreatorRequestDetail["examples"]["ads"][number];

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long a request stays open, as the message to render it with: the
 * component translates, so this stays plain TS and testable. "19h 55m left",
 * "4d 2h left", "5d left", "42m left", "Closed".
 */
export type TimeLeft =
  | { kind: "untilFilled" }
  | { kind: "closed" }
  | { kind: "daysHours"; days: number; hours: number }
  | { kind: "days"; days: number }
  | { kind: "hoursMinutes"; hours: number; minutes: number }
  | { kind: "minutes"; minutes: number }
  | { kind: "seconds"; seconds: number };

export function timeLeft(expiresAt: Date | null, now: Date): TimeLeft {
  if (!expiresAt) return { kind: "untilFilled" };
  const ms = expiresAt.getTime() - now.getTime();
  if (ms <= 0) return { kind: "closed" };
  const days = Math.floor(ms / DAY_MS);
  const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
  if (days > 0) {
    return hours > 0
      ? { kind: "daysHours", days, hours }
      : { kind: "days", days };
  }
  if (hours > 0) return { kind: "hoursMinutes", hours, minutes };
  if (minutes > 0) return { kind: "minutes", minutes };
  return { kind: "seconds", seconds: Math.ceil(ms / 1000) };
}

/** "12:40", 24-hour, as on the artboards. */
export function formatClock(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Where a request row sits: the home preview or the full feed. */
export type RequestRowVariant = "home" | "feed";

/** The "2 of 5 taken" figures, or null for an open-ended request. */
export function takenCount(
  item: CreatorFeedItem,
): { approved: number; target: number } | null {
  if (item.targetCount === null) return null;
  return { approved: item.approvedCount, target: item.targetCount };
}

/** How many more videos the request wants, or null for an open-ended one. */
export function wantedCount(item: CreatorFeedItem): number | null {
  if (item.targetCount === null) return null;
  return Math.max(0, item.targetCount - item.approvedCount);
}

/** "Tailgate odds · 0:31": the file name without its extension, and length. */
export function exampleLabel(ad: RequestExampleAd): string {
  const name = ad.fileName.replace(/\.[a-z0-9]{2,4}$/i, "");
  if (ad.videoLengthSeconds === null) return name;
  const total = Math.round(ad.videoLengthSeconds);
  return `${name} · ${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * Links into a request are scoped to the request's own program, not the one
 * the switcher shows: confirmSubmission refuses a request from another
 * program, and the feed spans every program the creator is in.
 */
export function requestHref(
  item: Pick<CreatorFeedItem, "id" | "orgSlug">,
  access: CreatorPortalAccess | null | undefined,
): string {
  return portalHref(`/one/requests/${item.id}`, {
    ...access,
    orgSlug: item.orgSlug,
  });
}

export function submitForRequestHref(
  item: Pick<CreatorFeedItem, "id" | "orgSlug">,
  access: CreatorPortalAccess | null | undefined,
): string {
  return portalHref(`/one/submit?request=${item.id}`, {
    ...access,
    orgSlug: item.orgSlug,
  });
}

/** The top-ranked open request other than the one just answered. */
export function pickNextUp(
  feed: CreatorFeedItem[] | undefined,
  answeredRequestId: string | null,
): CreatorFeedItem | null {
  return feed?.find((item) => item.id !== answeredRequestId) ?? null;
}

/** One checklist row; the component translates the row's title from `key`. */
export type SpecRow = {
  key: ContentRequestSpecParamKey;
  instruction: string;
  why: string | null;
  required: boolean;
  /** One of the five parameters shown first and expanded. */
  core: boolean;
};

const CORE_KEYS: readonly ContentRequestSpecParamKey[] =
  CONTENT_REQUEST_SPEC_CORE_KEYS;

/**
 * The spec as checklist rows: the five core parameters first (in their own
 * order), then the other required rows, then the rest, each in canonical
 * order; rows with nothing to say are left out.
 */
export function specRows(spec: ContentRequestSpec): SpecRow[] {
  const rows = CONTENT_REQUEST_SPEC_PARAM_KEYS.map((key) => {
    const parameter = spec.parameters[key];
    return {
      key,
      instruction: parameter.instruction.trim(),
      why: parameter.why?.trim() || null,
      required: parameter.required,
      core: CORE_KEYS.includes(key),
    };
  }).filter((row) => row.instruction.length > 0);
  const core = CORE_KEYS.flatMap((key) =>
    rows.filter((row) => row.key === key),
  );
  const rest = rows.filter((row) => !row.core);
  return [
    ...core,
    ...rest.filter((row) => row.required),
    ...rest.filter((row) => !row.required),
  ];
}

export type AttachedRequestFields = { contentRequestId?: string };

/**
 * What confirmSubmission receives for an attached request. A revision takes
 * its request from the take it revises, server-side, so it sends none.
 */
export function attachedRequestFields(input: {
  request: Pick<CreatorRequestDetail, "id"> | null;
  revisesSubmissionId: string | null;
}): AttachedRequestFields {
  if (!input.request || input.revisesSubmissionId) return {};
  return { contentRequestId: input.request.id };
}

export type SubmissionRequestFields = {
  status: string;
  contentRequest?: { id: string; title: string } | null;
  /** The minimum the video locked at upload: its request's guarantee. */
  payoutMinimumUsd: number;
};

/**
 * What a video card says about the request it answered: the guarantee it
 * locked, or null when it locked none (a new take of a rejected video whose
 * request had closed earns its share alone). Null for a video with no request.
 */
export function submissionRequestSummary(
  submission: SubmissionRequestFields,
): { requestId: string; title: string; guaranteeUsd: number | null } | null {
  const request = submission.contentRequest;
  if (!request) return null;
  return {
    requestId: request.id,
    title: request.title,
    guaranteeUsd:
      submission.payoutMinimumUsd > 0 ? submission.payoutMinimumUsd : null,
  };
}
